import React, { useState, useEffect } from "react";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import CustomButton from "../components/common/CustomButton";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { addJob, updateJob, deleteJob, getJobs } from "../utils/jobUtils";
import {
  TruckIcon,
  WrenchIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  BriefcaseIcon,
  ListIcon,
  MapPinIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    padding: "16px 24px",
    paddingTop: 16,
    minHeight: "100vh",
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 700,
  },
  searchInput: {
    marginBottom: 16,
  },
  jobItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  jobInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  jobIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  jobDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  jobName: {
    fontSize: 20,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 600,
  },
  jobNumber: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  jobDescription: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  jobAddress: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  jobRoles: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  jobInvoiceStatus: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 10,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    padding: 12,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    border: `1px solid ${COLORS.gray}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  switchLabel: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  switchIcon: {
    width: 20,
    height: 20,
    color: COLORS.primary,
  },
  button: {
    marginTop: 12,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    jobItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    actionsContainer: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
  },
};

const AdminJobsManagementScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [name, setName] = useState("");
  const [jobNumber, setJobNumber] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [roles, setRoles] = useState({
    isDriver: true,
    isEquipmentMan: true,
  });
  const [generateInvoice, setGenerateInvoice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchJobs = async () => {
    try {
      const jobList = await getJobs();
      // Sort jobs alphabetically by name
      const sortedJobs = jobList.sort((a, b) =>
        (a.name || "Unnamed").localeCompare(b.name || "Unnamed")
      );
      setJobs(sortedJobs);
    } catch (error) {
      alert("Failed to fetch jobs");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleSaveJob = async () => {
    if (!name || !jobNumber) {
      alert("Please fill in all required fields: Job Name and Job Number");
      return;
    }
    setLoading(true);
    try {
      const jobData = {
        name,
        jobNumber,
        description,
        address,
        roles,
        generateInvoice,
      };
      if (editingJob) {
        await updateJob(editingJob.id, jobData);
        alert("Job updated successfully");
      } else {
        await addJob(jobData);
        alert("Job added successfully");
      }
      setName("");
      setJobNumber("");
      setDescription("");
      setAddress("");
      setRoles({ isDriver: false, isEquipmentMan: false });
      setGenerateInvoice(true);
      setEditingJob(null);
      setModalVisible(false);
      await fetchJobs();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setName(job.name);
    setJobNumber(job.jobNumber || "");
    setDescription(job.description);
    setAddress(job.address || "");
    setRoles(job.roles);
    setGenerateInvoice(job.generateInvoice !== false); // Default to true if undefined
    setModalVisible(true);
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteJob(id);
      alert("Job deleted successfully");
      await fetchJobs();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Filter jobs based on search query
  const filteredJobs = (jobs || []).filter((job) =>
    (job.name || "Unnamed").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderJobItem = (job) => (
    <div style={styles.jobItem} key={job.id}>
      <div style={styles.jobInfo}>
        <BriefcaseIcon style={styles.jobIcon} />
        <div style={styles.jobDetails}>
          <span style={styles.jobName}>{job.name}</span>
          <span style={styles.jobNumber}>#{job.jobNumber}</span>
          <span style={styles.jobDescription}>{job.description}</span>
          {job.address && <span style={styles.jobAddress}>{job.address}</span>}
          <span style={styles.jobRoles}>
            {job.roles.isDriver && job.roles.isEquipmentMan
              ? "Driver & Equipment Man"
              : job.roles.isDriver
              ? "Driver"
              : job.roles.isEquipmentMan
              ? "Equipment Man"
              : "No roles assigned"}
            {(job.roles.isDriver || job.roles.isEquipmentMan) && " ("}
            {job.roles.isDriver && (
              <TruckIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {job.roles.isDriver && job.roles.isEquipmentMan && ", "}
            {job.roles.isEquipmentMan && (
              <WrenchIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {(job.roles.isDriver || job.roles.isEquipmentMan) && ")"}
          </span>
          <span style={styles.jobInvoiceStatus}>
            Invoices: {job.generateInvoice !== false ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>
      <div style={styles.actionsContainer}>
        <button
          style={{ ...styles.actionButton }}
          onClick={() => handleEditJob(job)}
        >
          <PencilIcon width={24} height={24} stroke={COLORS.primary} />
        </button>
        <button
          style={{ ...styles.actionButton, ...styles.deleteButton }}
          onClick={() => handleDeleteJob(job.id)}
          disabled={deletingId === job.id}
        >
          {deletingId === job.id ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <TrashIcon width={24} height={24} stroke={COLORS.error} />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>Jobs Management</h1>
        </div>
        <CustomInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          icon="search"
          placeholder="Search by job name"
          style={styles.searchInput}
        />
        {fetching ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span>Loading...</span>
          </div>
        ) : (
          <div>
            {filteredJobs.length === 0 ? (
              <span style={styles.emptyText}>No jobs available</span>
            ) : (
              filteredJobs.map(renderJobItem)
            )}
          </div>
        )}
        <button
          style={styles.addButton}
          onClick={() => {
            setEditingJob(null);
            setName("");
            setJobNumber("");
            setDescription("");
            setAddress("");
            setRoles({ isDriver: false, isEquipmentMan: false });
            setGenerateInvoice(true);
            setModalVisible(true);
          }}
          disabled={loading}
        >
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <CustomModal
          isVisible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title={editingJob ? "Edit Job" : "Add Job"}
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <CustomInput
            label="Job Name"
            value={name}
            onChangeText={setName}
            icon="label"
            placeholder="Enter job name"
            style={{ marginBottom: 12 }}
          />
          <CustomInput
            label="Job Number"
            value={jobNumber}
            onChangeText={setJobNumber}
            icon="list"
            placeholder="Enter job number"
            style={{ marginBottom: 12 }}
          />
          <CustomInput
            label="Description"
            value={description}
            onChangeText={setDescription}
            icon="description"
            placeholder="Enter job description"
            multiline
            style={{ marginBottom: 12 }}
          />
          <CustomInput
            label="Address"
            value={address}
            onChangeText={setAddress}
            icon="map-pin"
            placeholder="Enter job address"
            style={{ marginBottom: 12 }}
          />
          <div style={styles.switchContainer}>
            <span style={styles.switchLabel}>
              <TruckIcon style={styles.switchIcon} />
              Driver
            </span>
            <input
              type="checkbox"
              checked={roles.isDriver}
              onChange={(e) =>
                setRoles({ ...roles, isDriver: e.target.checked })
              }
              style={{ cursor: "pointer" }}
            />
          </div>
          <div style={styles.switchContainer}>
            <span style={styles.switchLabel}>
              <WrenchIcon style={styles.switchIcon} />
              Equipment Man
            </span>
            <input
              type="checkbox"
              checked={roles.isEquipmentMan}
              onChange={(e) =>
                setRoles({ ...roles, isEquipmentMan: e.target.checked })
              }
              style={{ cursor: "pointer" }}
            />
          </div>
          <div style={styles.switchContainer}>
            <span style={styles.switchLabel}>
              <DocumentTextIcon style={styles.switchIcon} />
              Generate Invoice
            </span>
            <input
              type="checkbox"
              checked={generateInvoice}
              onChange={(e) => setGenerateInvoice(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
          </div>
          <CustomButton
            title={
              loading ? "Saving..." : editingJob ? "Update Job" : "Create Job"
            }
            onPress={handleSaveJob}
            style={styles.button}
            disabled={loading}
          />
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminJobsManagementScreen;
